import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, AlertCircle, Zap, FileText, Edit3, CheckCircle2, Server, Wrench } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function FAQ() {
  const {
    t,
    language
  } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = language === 'es' ? [{
    question: '¿En serio en 48 horas?',
    answer: 'Sí. Una vez confirmado el pago y recibida tu información básica, tu web estará lista. Llevamos +127 proyectos entregados a tiempo.',
    icon: Zap,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10'
  }, {
    question: '¿Qué necesito darles?',
    answer: 'Para empezar, el pago debe ser realizado primero. Una vez confirmado el pago, te mandamos un link con un formulario donde nos proporcionas: tu logo (o lo creamos), colores de tu marca, textos básicos de tu negocio y fotos si tienes. Nosotros hacemos el resto.',
    icon: FileText,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10'
  }, {
    question: '¿Puedo cambiar cosas después?',
    answer: 'Incluye 1 ronda de revisiones. Para cambios continuos, existe el plan de mantenimiento.',
    icon: Edit3,
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10'
  }, {
    question: '¿Y si no me gusta el resultado?',
    answer: 'Trabajamos contigo hasta que estés satisfecho. Y si no, garantía total de reembolso.',
    icon: CheckCircle2,
    color: 'text-green-400',
    bgColor: 'bg-green-400/10'
  }, {
    question: '⚠️ ¿El hosting y dominio están incluidos?',
    answer: 'NO. El hosting y el dominio NUNCA están incluidos en el precio. Son servicios externos que debes contratar por tu cuenta (recomendamos Hostinger, desde €2-5/mes). Nosotros te ayudamos con la configuración y te asesoramos en todo el proceso, pero el pago del hosting y dominio es aparte.',
    icon: Server,
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    highlighted: true
  }, {
    question: '¿Puedo añadir mantenimiento después?',
    answer: 'Sí, en cualquier momento puedes sumarte a un plan mensual con descuento por ser cliente web.',
    icon: Wrench,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/10'
  }] : [{
    question: 'Really in 48 hours?',
    answer: 'Yes. Once payment is confirmed and we receive your basic information, your website will be ready. We have delivered over 127 projects on time.',
    icon: Zap,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10'
  }, {
    question: 'What do I need to provide?',
    answer: 'To get started, payment must be made first. Once payment is confirmed, we send you a link with a form where you provide: your logo (or we create it), brand colors, basic business texts and photos if you have them. We do the rest.',
    icon: FileText,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10'
  }, {
    question: 'Can I change things later?',
    answer: 'Includes 1 round of revisions. For continuous changes, there is a maintenance plan.',
    icon: Edit3,
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10'
  }, {
    question: "What if I don't like the result?",
    answer: 'We work with you until you are satisfied. And if not, full money-back guarantee.',
    icon: CheckCircle2,
    color: 'text-green-400',
    bgColor: 'bg-green-400/10'
  }, {
    question: '⚠️ Is hosting and domain included?',
    answer: 'NO. Hosting and domain are NEVER included in the price. These are external services you must contract on your own (we recommend Hostinger, from €2-5/month). We help you with the setup and advise you through the entire process, but hosting and domain payment is separate.',
    icon: Server,
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    highlighted: true
  }, {
    question: 'Can I add maintenance later?',
    answer: 'Yes, you can join a monthly plan at any time with a discount for being a web client.',
    icon: Wrench,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-400/10'
  }];
  return <section id="faq" className="py-16 md:py-24 bg-brand-darker relative overflow-hidden" data-id="element-69">
      {/* Decorative elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-cyan/5 rounded-full blur-3xl" data-id="element-70" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-purple/5 rounded-full blur-3xl" data-id="element-71" />

      <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10" data-id="element-72">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12 md:mb-16" data-id="element-73">
          <div className="inline-flex items-center gap-2 bg-brand-cyan/10 border border-brand-cyan/20 rounded-full px-4 py-2 mb-4" data-id="element-74">
            <HelpCircle className="w-4 h-4 text-brand-cyan" data-id="element-75" />
            <span className="text-sm font-bold text-brand-cyan uppercase tracking-wider" data-id="element-76">
              FAQ
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-id="element-77">
            {t('faq.title')}
          </h2>
          <p className="text-gray-400 text-base md:text-lg" data-id="element-78">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-79">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          delay: index * 0.1
        }} className={`relative rounded-2xl overflow-hidden transition-all duration-300 ${faq.highlighted ? 'bg-gradient-to-r from-red-500/10 to-orange-500/10 border-2 border-red-500/30 shadow-[0_0_30px_rgba(239,68,68,0.15)]' : 'bg-brand-bg border border-white/10 hover:border-brand-cyan/30'}`} data-id="element-80">
              {faq.highlighted && <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg flex items-center gap-1" data-id="element-81">
                  <AlertCircle className="w-3 h-3" data-id="element-82" />
                  {language === 'es' ? 'IMPORTANTE' : 'IMPORTANT'}
                </div>}

              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full flex items-center justify-between p-5 md:p-6 text-left focus:outline-none group" data-id="element-83">
                <div className="flex items-center gap-3 md:gap-4 flex-1" data-id="element-84">
                  <motion.div animate={{
                rotate: openIndex === index ? 360 : 0
              }} transition={{
                duration: 0.3
              }} className={`shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${faq.bgColor}`} data-id="element-85">
                    <faq.icon className={`w-5 h-5 md:w-6 md:h-6 ${faq.color}`} data-id="element-86" />
                  </motion.div>
                  <span className={`text-base md:text-lg font-semibold ${faq.highlighted ? 'text-white' : 'text-white group-hover:text-brand-cyan transition-colors'}`} data-id="element-87">
                    {faq.question}
                  </span>
                </div>
                <motion.div animate={{
              rotate: openIndex === index ? 180 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-88">
                  <ChevronDown className={`w-5 h-5 shrink-0 ${faq.highlighted ? 'text-red-400' : 'text-brand-cyan'}`} data-id="element-89" />
                </motion.div>
              </button>

              <AnimatePresence data-id="element-90">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} data-id="element-91">
                    <motion.div initial={{
                y: -10
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.3
              }} className={`px-5 md:px-6 pb-5 md:pb-6 leading-relaxed border-t pt-4 ${faq.highlighted ? 'text-gray-200 border-red-500/20 bg-red-500/5' : 'text-gray-400 border-white/5'}`} data-id="element-92">
                      {faq.answer}
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        {/* Additional clarification box */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.6
      }} className="mt-8 md:mt-12 bg-gradient-to-r from-brand-cyan/10 to-brand-purple/10 border border-brand-cyan/20 rounded-2xl p-6 md:p-8" data-id="element-93">
          <div className="flex items-start gap-4" data-id="element-94">
            <div className="shrink-0 w-10 h-10 md:w-12 md:h-12 bg-brand-cyan/20 rounded-full flex items-center justify-center" data-id="element-95">
              <HelpCircle className="w-5 h-5 md:w-6 md:h-6 text-brand-cyan" data-id="element-96" />
            </div>
            <div data-id="element-97">
              <h3 className="text-lg md:text-xl font-bold text-white mb-2" data-id="element-98">
                {language === 'es' ? '¿Tienes más preguntas?' : 'Have more questions?'}
              </h3>
              <p className="text-gray-400 mb-4" data-id="element-99">
                {language === 'es' ? 'Estamos aquí para ayudarte. Escríbenos y te respondemos en minutos.' : "We're here to help. Write to us and we'll respond in minutes."}
              </p>
              <a href="#contacto" className="inline-flex items-center gap-2 bg-brand-cyan hover:bg-brand-cyan/90 text-brand-darker font-bold px-6 py-3 rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(0,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,255,255,0.5)] hover:scale-105" data-id="element-100">
                {language === 'es' ? 'Contáctanos ahora' : 'Contact us now'}
                <ChevronDown className="w-4 h-4 -rotate-90" data-id="element-101" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}